// @flow
import React, { Component } from 'react';
import { t } from '@lingui/macro';
import type { Address } from '@rover/react-lib/types';
import inject from '@rover/utilities/injector';

import Form from '../../utils/Form';
import FormGroup from '../../formFields/FormGroup';
import FormValidationError from '../../formFields/FormValidationError';
import StripeCreditCardForm from '../StripeCreditCardForm';

import SelectCreditCardForm from './SelectCreditCardForm';
import BraintreeCreditCardForm from './BraintreeCreditCardForm';
import type { CreditCardSummary, CreditCardAddSummary } from '../../../types/CreditCard';
import SuccessOrErrorAlert from '../../notifications/SuccessOrErrorAlert';

const useStripeContext = inject('Rover.context.useStripe', false);

type State = {
  addingCard: boolean,
};

export type Props = {
  address?: Address,
  onAddCard: ($Shape<CreditCardAddSummary>) => void,
  loading: boolean,
  hideAddCardCancelButton?: boolean,
  partialPostalRegExp?: ?RegExp,
  fullPostalRegExp?: ?RegExp,
  getStateFieldNameByPostalCode?: string => string,
  isStateFieldRequiredByPostalCode?: string => boolean,
  supportedCountryCodes: Array<string>,
  language: string,

  creditCards: Array<CreditCardSummary>,
  selectedCardId: string,
  onChange: string => void,
  onDeleteCard: string => void,
  radioName?: string,

  validationErrors: any,
  nestedForm: boolean,
  errorMessage: string,

  onReportFormVisibility: boolean => void,

  disabled: boolean,

  useStripe: boolean,
  isSubmitting: boolean,
  centerButtons?: boolean,
  saveButtonText?: string,
};

export default class CreditCardForm extends Component<Props, State> {
  static defaultProps = {
    step: 0,
    loading: false,
    hideAddCardCancelButton: undefined,
    nestedForm: false,
    getStateFieldNameByPostalCode: () => '',
    isStateFieldRequiredByPostalCode: () => true,

    creditCards: [],
    selectedCardId: '',
    addingCard: false,
    validationErrors: undefined,
    onReportFormVisibility: () => {},

    disabled: false,
    centerButtons: false,

    useStripe: useStripeContext,
  };

  static validators = {
    selectedCardId: Form.validators.required(t`Credit card is required`),
  };

  state = {
    addingCard: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { selectedCardId, onChange } = this.props;
    if (selectedCardId && !prevProps.selectedCardId) {
      onChange(selectedCardId);
    }
  }

  handleToggle = (isAdding: boolean) => {
    const { onReportFormVisibility } = this.props;
    onReportFormVisibility(isAdding);
    this.setState(() => ({
      addingCard: isAdding,
    }));
  };

  render() {
    const {
      address,
      // add card
      onAddCard,
      loading,
      hideAddCardCancelButton,
      partialPostalRegExp,
      fullPostalRegExp,
      getStateFieldNameByPostalCode,
      isStateFieldRequiredByPostalCode,
      supportedCountryCodes,
      language,

      // select card
      creditCards,
      selectedCardId,
      onDeleteCard,
      onChange,
      radioName,

      validationErrors,
      nestedForm,
      errorMessage,

      onReportFormVisibility,
      disabled,

      useStripe,
      isSubmitting,
      centerButtons,
      saveButtonText,

      ...others
    } = this.props;
    const { addingCard } = this.state;
    let creditCardElement;
    if (addingCard || (!loading && creditCards.length === 0)) {
      const formProps = {
        address,
        onSubmit: onAddCard,
        onToggleForm: () => this.handleToggle(false),
        validationErrors,
        loading,
        nestedForm,
        hideCancelButton: hideAddCardCancelButton,
        fullPostalRegExp,
        partialPostalRegExp,
        getStateFieldNameByPostalCode,
        isStateFieldRequiredByPostalCode,
        isSubmitting,
        supportedCountryCodes,
        language,
        centerButtons,
        saveButtonText,
      };
      if (useStripe) {
        creditCardElement = <StripeCreditCardForm {...formProps} />;
      } else {
        creditCardElement = <BraintreeCreditCardForm {...formProps} />;
      }
    } else {
      creditCardElement = (
        <SelectCreditCardForm
          cards={creditCards}
          value={selectedCardId}
          onChange={onChange}
          onDelete={onDeleteCard}
          onToggleForm={() => this.handleToggle(true)}
          radioName={radioName}
          disabled={disabled}
        />
      );
    }
    return (
      <FormGroup {...others}>
        <FormValidationError
          message={
            validationErrors && (validationErrors.selectedCardId || validationErrors.formVisible)
          }
        >
          <SuccessOrErrorAlert error={errorMessage} />
          {creditCardElement}
        </FormValidationError>
      </FormGroup>
    );
  }
}



// WEBPACK FOOTER //
// ../react-lib/src/components/forms/CreditCardForm/CreditCardForm.jsx